import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Dumbbell, Sofa, ShieldCheck, Users, KeyRound, Star } from "lucide-react";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import PropertyCard from "@/components/PropertyCard";

// Below-the-fold; defer the heavy logo ticker until after hero paints.
const ResidentsMarquee = React.lazy(() => import("@/components/ResidentsMarquee"));

const FEATURES = [
  { icon: KeyRound, title: "Zero brokerage", body: "Skip the broker. Move in fast.", color: "#FFD166" },
  { icon: Sofa, title: "Fully furnished", body: "Bed, Wardrobes, kitchen — sorted.", color: "#20E3B2" },
  { icon: Dumbbell, title: "Premium features", body: "Gym, Swimming Pool, Garden — peace of mind.", color: "#4D4DFF" },
  { icon: ShieldCheck, title: "Low deposits", body: "Start from just one month. That's it.", color: "#FF6B6B" },
  { icon: Users, title: "Curated flatmates", body: "Verified, like-minded pros.", color: "#FFD166" },
  { icon: Sparkles, title: "Hassle-free", body: "Repairs & Maintenance included.", color: "#20E3B2" },
];

const TESTIMONIALS = [
  { name: "Aarav K.", role: "Product @ Mastercard", text: "Moved from Bangalore in 3 days. Zero broker drama, brilliant flatmates, walk to office.", color: "#FFD166" },
  { name: "Priya S.", role: "Designer @ Persistent", text: "The community is unreal. We cook, we party, we work. Genuinely feels like home.", color: "#20E3B2" },
  { name: "Rohan M.", role: "Engineer @ TCS", text: "Single occupancy, fully furnished, low deposit. Locked it in a single phone call.", color: "#FF6B6B" },
];

export default function Home() {
  const [props, setProps] = useState([]);
  const [banners, setBanners] = useState(null);

  useEffect(() => {
    api.get("/properties", { params: { limit: 6 } })
      .then((r) => setProps(r.data || []))
      .catch(() => setProps([]));
    api.get("/cms/banners")
      .then((r) => setBanners(r.data?.slots || null))
      .catch(() => setBanners(null));
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 space-y-6">
            <span className="badge-pill bg-[#FFD166]">★ Pune's multiliving OG</span>
            <h1 className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
              Renting<br/>
              that <span className="bg-[#FF4D4D] text-white px-2 -rotate-2 inline-block">feels</span> like<br/>
              <span className="italic text-[#4D4DFF]">home.</span>
            </h1>
            <p className="text-lg text-[#4A4A5A] max-w-xl">
              Fully-furnished shared flats in gated societies next to Pune's IT hubs. Zero brokerage, low deposits, ridiculously good flatmates.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/properties" data-testid="hero-cta-explore" className="brutal-btn">Find your home <ArrowRight size={18}/></Link>
              <Link to="/contact" data-testid="hero-cta-visit" className="brutal-btn brutal-btn-white">Book a free visit</Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-6 text-sm">
              {[["1780Cr+","AUM"],["1900+","Happy tenants"],["5Cr+","Brokerage saved"],["100%","Verified flatmates"]].map(([n,l]) => (
                <div key={l}>
                  <div className="font-display font-extrabold text-2xl">{n}</div>
                  <div className="text-xs uppercase tracking-widest text-[#4A4A5A]">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 grid-rows-3 gap-4 min-h-[460px]">
            <BannerSlot slot={banners?.[0]} fallbackImage="https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&q=80" alt="Multiliving lounge" big/>
            <BannerSlot slot={banners?.[1]} fallbackImage="https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=900&q=80" alt="Cozy room"/>
            <div className="relative brutal-card overflow-hidden bg-[#FFD166] grid place-items-center p-4 text-center">
              <div>
                <Sparkles className="mx-auto mb-2" size={28}/>
                <div className="font-display font-extrabold text-lg leading-tight">Move in<br/>this weekend</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee/>

      <React.Suspense fallback={<div className="h-32"/>}>
        <ResidentsMarquee/>
      </React.Suspense>

      {/* FEATURED PROPERTIES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="section-featured-properties">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <span className="badge-pill bg-[#20E3B2] mb-3">Hand-picked</span>
            <h2 className="font-display font-extrabold text-4xl sm:text-5xl">Find your<br/>convenient home</h2>
          </div>
          <Link to="/properties" data-testid="cta-view-all" className="brutal-btn brutal-btn-secondary">Browse all homes →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {props.map((p, i) => <PropertyCard key={p.id} p={p} index={i}/>)}
        </div>
      </section>

      {/* WHY THC */}
      <section className="bg-[#0A0A14] text-white border-y-2 border-black" data-testid="section-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <span className="badge-pill bg-[#FFD166]">What makes us different</span>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-4 max-w-2xl">Built for renters, not realtors.</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {FEATURES.map((f, i) => (
              <div key={f.title} className="brutal-card text-black p-6" style={{ background: f.color, animation: `fadeUp 0.6s ${i*70}ms both` }}>
                <f.icon size={28}/>
                <div className="font-display font-extrabold text-xl mt-4">{f.title}</div>
                <div className="text-sm mt-1 opacity-80">{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-testid="section-testimonials">
        <span className="badge-pill bg-[#FF6B6B] text-white">★ Reviews</span>
        <h2 className="font-display font-extrabold text-4xl sm:text-5xl mt-4">People who <span className="italic">actually</span> live here.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {TESTIMONIALS.map((t, i) => (
            <div key={t.name} className="brutal-card p-6 flex flex-col gap-4" style={{ animation: `fadeUp 0.6s ${i*80}ms both` }}>
              <div className="flex gap-1 text-[#FF4D4D]">{[...Array(5)].map((_,k)=><Star key={k} size={16} fill="currentColor"/>)}</div>
              <p className="text-base leading-relaxed">"{t.text}"</p>
              <div className="mt-auto pt-3 border-t-2 border-dashed border-black/20">
                <div className="font-display font-bold">{t.name}</div>
                <div className="text-xs text-[#4A4A5A]">{t.role}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PARTNERS strip */}
      <section className="bg-[#F3F2EE] border-y-2 border-black py-12" data-testid="section-partners">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-[#4A4A5A] mb-6">Trusted by industry leaders</p>
          <div className="flex flex-wrap justify-center items-center gap-8 font-display font-extrabold text-2xl text-[#0A0A14]/70">
            <span>SOBHA</span><span>·</span>
            <span>EQARO</span><span>·</span>
            <span>IDFY</span><span>·</span>
            <span>BRIGADE</span><span>·</span>
            <span>IIT MANDI</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function BannerSlot({ slot, fallbackImage, alt, big = false }) {
  const isOffer = slot?.type === "offer";
  const image = (slot?.image && slot.image.length > 0) ? slot.image : fallbackImage;
  const cls = `relative brutal-card overflow-hidden ${big ? "col-span-2 row-span-2" : ""}`;
  const slotIdx = slot?.slot || (big ? 1 : 2);

  const inner = (
    <>
      <img src={image} alt={slot?.title || alt} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      {isOffer && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent p-4 flex flex-col justify-end text-white">
          {slot.badge && (
            <span className="badge-pill bg-[#FFD166] text-black self-start mb-2 text-[10px]">{slot.badge}</span>
          )}
          {slot.title && <div className="font-display font-extrabold text-2xl leading-tight">{slot.title}</div>}
          {slot.subtitle && <div className="text-sm opacity-90 mt-1">{slot.subtitle}</div>}
        </div>
      )}
    </>
  );

  if (isOffer && slot.link) {
    return (
      <a href={slot.link} data-testid={`banner-slot-${slotIdx}`} data-banner-type="offer" className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={`banner-slot-${slotIdx}`} data-banner-type={slot?.type || "image"} className={cls}>
      {inner}
    </div>
  );
}
